import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { getAnalyticsConsent, onConsentChange } from '@/lib/analytics-consent';

declare global {
  interface Window {
    dataLayer?: unknown[][];
    gtag?: (...args: unknown[]) => void;
  }
}

const MEASUREMENT_ID = 'G-GLE5FLNHV2';
const SCRIPT_ID = 'gtag-script';

function loadGoogleTag(pagePath: string): void {
  if (document.getElementById(SCRIPT_ID)) return;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = (...args: unknown[]) => window.dataLayer?.push(args);
  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID, { send_page_view: true, page_path: pagePath });

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  script.async = true;
  document.head.appendChild(script);
}

/**
 * Loads Google measurement only after consent and a meaningful visitor action.
 * This keeps third-party marketing code out of the initial rendering path.
 */
export default function GoogleAnalytics() {
  const location = useLocation();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const activate = () => {
      if (!getAnalyticsConsent()) return;
      loadGoogleTag(location.pathname + location.search);
      setReady(true);
      events.forEach((event) => window.removeEventListener(event, activate));
    };
    // Scrolling can be initiated by browsers and audit tools, so only use
    // deliberate input to keep analytics off the initial rendering path.
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart'];

    if (getAnalyticsConsent()) {
      events.forEach((event) => window.addEventListener(event, activate, { passive: true, once: true }));
    }

    const cleanupConsent = onConsentChange((consented) => {
      if (consented) activate();
    });

    return () => {
      events.forEach((event) => window.removeEventListener(event, activate));
      cleanupConsent();
    };
  }, []);

  useEffect(() => {
    if (ready && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', { page_path: location.pathname + location.search });
    }
  }, [location.pathname, location.search, ready]);

  return null;
}
